import React, { useState } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import { useDarkMode } from '../../context/DarkModeContext';
import { useSelector } from 'react-redux';
import { getCourier, getCourierSetting, getStoreLocations } from '../../state/courierSlice';
import { ArrowRightIcon, ArrowLeftIcon, Gift, FileText } from 'lucide-react';
import { useAccountCreation, useEmailVerification, useTurnstile } from '../hooks';
import { useReferral } from '../../hooks/useReferral';
import { OTPInput, Modal } from '@shared/components';
import { Button, InputField, getCountryNameOptions } from '@shared/components';
import { showSuccessToast } from '../../utils/toast';
import { validateTrn, formatTrn } from '../../utils/trnValidation';
import { PartnerLinkApi } from '@shared/sdk-v1/apis';
import { apiConfig } from '../../shared/api-config';
import { useTrackEvent } from '../../utils/track';
import {
  isPhoneVerificationAvailable,
  finalizeSignupAfterEmail,
} from '../../utils/signupTracking';

interface CreateAccountFormProps {
  onToggleMode: () => void;
  primaryColor: string;
  referralId?: string | null;
  initialEmail?: string;
  initialPhone?: string;
  source?: string;
  tier?: string;
  bundle?: string;
  businessName?: string;
  contactName?: string;
}

interface UserData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  storeLocationId?: number;
  trnNumber?: string;
  businessName?: string;
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  country?: string;
  referralSource?: string;
  wasReferred?: boolean;
  referrerQuery?: string;
}

const CreateAccountForm = ({
  onToggleMode,
  primaryColor,
  referralId,
  initialEmail = '',
  initialPhone = '',
  source = '',
  tier = '',
  bundle = '',
  businessName: initialBusinessName = '',
  contactName = '',
}: CreateAccountFormProps) => {
  const { isDarkMode } = useDarkMode();
  const courier = useSelector(getCourier);
  const courierSetting = useSelector(getCourierSetting);
  const rewardSetting = courier?.rewardSetting;
  const legalDocument = courier?.legalDocument;
  const storeLocations = useSelector(getStoreLocations);
  const track = useTrackEvent();
  const requireTrnOnSignup = courierSetting?.requireTrnOnSignup ?? false;
  const requireAddressOnSignup = courierSetting?.requireAddressOnSignup ?? false;
  const isReferredByEnabled = courierSetting?.isReferredByEnabled ?? false;
  const isReferralSourceEnabled = courierSetting?.isReferralSourceEnabled ?? false;
  const referralSourceOptions = courierSetting?.referralSourceOptions ?? [];
  const isQualifyRedirect = !!source;
  const isWarehouseApiEnabled = courierSetting?.isWarehouseApiEnabled ?? false;

  // Parse contactName into first/last
  const [initialFirstName, initialLastName] = (() => {
    if (!contactName) return ['', ''];
    const parts = contactName.trim().split(/\s+/);
    if (parts.length === 1) return [parts[0], ''];
    return [parts[0], parts.slice(1).join(' ')];
  })();

  const [step, setStep] = useState<1 | 2>(1);
  const [userData, setUserData] = useState<UserData>({
    firstName: initialFirstName,
    lastName: initialLastName,
    email: initialEmail,
    phone: initialPhone,
    password: '',
    storeLocationId: undefined,
    trnNumber: '',
    businessName: initialBusinessName,
  });

  // Auto-select store location if there's only one or if there's a primary location
  React.useEffect(() => {
    if (storeLocations.length === 1) {
      setUserData(prev => ({
        ...prev,
        storeLocationId: storeLocations[0].id
      }));
    } else if (storeLocations.length > 1) {
      const primaryLocation = storeLocations.find(location => location.isPrimaryLocation);
      if (primaryLocation && !userData.storeLocationId) {
        setUserData(prev => ({
          ...prev,
          storeLocationId: primaryLocation.id
        }));
      }
    }
  }, [storeLocations, userData.storeLocationId]);
  const [userId, setUserId] = useState<string>('');
  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    storeLocationId: '',
    agreements: '',
    trn: '',
    street1: '',
    city: ''
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const { createAccount, sendEmailVerification, loginUser, isLoading, isSendingVerification } = useAccountCreation();
  const { verifyEmailCode } = useEmailVerification();
  const turnstileSiteKey = courierSetting?.turnstileSiteKey;
  const { siteKey, token: turnstileToken, isReady: turnstileReady, hasError: turnstileError, onSuccess, onError, onExpire, reset: resetTurnstile, widgetRef } = useTurnstile(turnstileSiteKey);


  // Validation function
  const validateForm = () => {
    const newErrors = {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      storeLocationId: '',
      agreements: '',
      trn: '',
      street1: '',
      city: ''
    };

    // First name validation
    if (!userData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (userData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    }

    // Last name validation
    if (!userData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (userData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    }

    // Email validation
    if (!userData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation
    if (!userData.phone) {
      newErrors.phone = 'Phone number is required';
    } else if (userData.phone.replace(/\D/g, '').length < 10) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    // Password validation
    if (!userData.password) {
      newErrors.password = 'Password is required';
    } else if (userData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(userData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }

    // Store location validation (only if there are multiple locations)
    if (storeLocations.length > 1 && !userData.storeLocationId) {
      newErrors.storeLocationId = 'Please select a store location';
    }

    // TRN validation (only if required)
    if (requireTrnOnSignup) {
      if (!userData.trnNumber?.trim()) {
        newErrors.trn = 'TRN is required';
      } else {
        const trnValidation = validateTrn(userData.trnNumber);
        if (!trnValidation.valid) {
          newErrors.trn = trnValidation.error || 'Please enter a valid TRN';
        }
      }
    }

    // Address validation (only if required)
    if (requireAddressOnSignup) {
      if (!userData.street1?.trim()) {
        newErrors.street1 = 'Street address is required';
      }
      if (!userData.city?.trim()) {
        newErrors.city = 'City is required';
      }
    }

    // Terms and privacy validation
    if (!agreedToTerms || !agreedToPrivacy) {
      newErrors.agreements = 'You must agree to the Terms of Agreement and Privacy Policy';
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  // Check if form is valid for button state
  const isTrnValid = !requireTrnOnSignup || (userData.trnNumber?.trim() && validateTrn(userData.trnNumber).valid);
  const isAddressValid = !requireAddressOnSignup || (userData.street1?.trim() && userData.city?.trim());
  const isFormValid = userData.firstName.trim() &&
                     userData.lastName.trim() &&
                     userData.email &&
                     userData.phone &&
                     userData.password &&
                     (storeLocations.length <= 1 || userData.storeLocationId) &&
                     isTrnValid &&
                     isAddressValid &&
                     agreedToTerms &&
                     agreedToPrivacy &&
                     (turnstileReady || !siteKey) &&
                     !Object.values(errors).some(error => error);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      // The phone InputField emits a canonical E.164 string (e.g. +447712345678).
      setUserData(prev => ({
        ...prev,
        [name]: value
      }));
    } else if (name === 'storeLocationId') {
      setUserData(prev => ({
        ...prev,
        [name]: value ? parseInt(value, 10) : undefined
      }));
    } else if (name === 'trnNumber') {
      // Format TRN as XXX-XXX-XXX
      setUserData(prev => ({
        ...prev,
        [name]: formatTrn(value)
      }));
    } else {
      setUserData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const { createReferral } = useReferral();
  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();

    if (step === 1) {
      if (!validateForm()) {
        return;
      }
      const accountData = isQualifyRedirect
        ? { ...userData, isCourier: true }
        : userData;
      const result = await createAccount(accountData, turnstileToken || undefined, turnstileSiteKey);

      if (!result.success) {
        resetTurnstile();
      }

      if (result.success && result.needsVerification && result.userId) {
        setUserId(result.userId);

        // Create referral after successful account creation
        if (referralId && result.userId && courier?.id) {
          try {
            await createReferral(parseInt(referralId), parseInt(result.userId), courier.id);
          } catch (error) {
            console.error('Error creating referral:', error);
          }
        }

        // Auto-create PartnerLink for qualification redirect signups
        if (source === 'qualify' && courier?.id && result.userId) {
          try {
            const partnerLinkApi = new PartnerLinkApi(apiConfig());
            await partnerLinkApi.partnerLinkCreatePartnerLink({
              body: {
                warehouseTenantId: courier.id.toString(),
                courierEmail: userData.email,
                creatorId: result.userId,
              },
            });
          } catch (error) {
            console.error('Error creating partner link:', error);
          }
        }

        setStep(2);
      }
    }
  };

      const verifyCode = async (code: string) => {
    try {
      const result = await verifyEmailCode(code, userId);

      if (result.success) {
        // If there's a referral ID, create the referral
        // if (referralId && userId && courier?.id) {
        //   try {
        //     await createReferral(parseInt(referralId), parseInt(userId), courier.id);
        //   } catch (error) {
        //     console.error('Error creating referral:', error);
        //     // Don't block the signup process if referral creation fails
        //   }
        // }

        // Login the user with their credentials
        await loginUser(userData.email, userData.password);

        // Email is verified. If the courier can also verify phone, defer the
        // sign_up event until phone verification completes; otherwise email is
        // the furthest completion, so count the signup now.
        finalizeSignupAfterEmail(
          isPhoneVerificationAvailable(courierSetting),
          userId,
          track
        );

        // Show success message
        showSuccessToast({
          title: 'Success',
          message: 'Account created and verified successfully!'
        });
      }
    } catch (error) {
      console.error('Error during verification:', error);
      showSuccessToast({
        title: 'Error',
        message: 'Failed to verify account. Please try again.'
      });
    }
  };

  const handleResendCode = async () => {
    if (userId && !isSendingVerification) {
      await sendEmailVerification(userId);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    } else {
      onToggleMode();
    }
  };

  return (
    <div className="w-full max-w-md">
      <button
        type="button"
        onClick={handleBack}
        className="mb-6 mt-8 sm:mt-2 md:mt-0 flex items-center text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white py-2 z-10 relative bg-white/80 dark:bg-gray-800/60 backdrop-blur-sm"
        style={!isDarkMode ? { color: primaryColor } : undefined}
      >
        <ArrowLeftIcon className="h-5 w-5 mr-2" />
        {step === 1 ? 'Back to login' : 'Back to personal info'}
      </button>

      {step === 1 ? (
        <>
          {referralId && (
            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center">
                <Gift className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
                <p className="text-green-800 dark:text-green-300">
                  You're signing up with a referral! You'll receive {rewardSetting?.recruitedPoints ? rewardSetting.recruitedPoints / 2 : 0} Gems when you create your account.
                </p>
              </div>
            </div>
          )}
          <h2 className="text-2xl font-bold mb-6 text-center text-gray-900 dark:text-gray-100">
            {isQualifyRedirect && isWarehouseApiEnabled ? 'Confirm Your Details' : 'Create a new account'}
          </h2>
          {isQualifyRedirect && isWarehouseApiEnabled && (
            <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-blue-800 dark:text-blue-300 text-sm">
                Almost there! Please confirm your details below to complete your account setup.
              </p>
            </div>
          )}
          <form onSubmit={handleContinue} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <InputField
                  label="First Name"
                  name="firstName"
                  type="text"
                  value={userData.firstName}
                  onChange={handleInputChange}
                  placeholder="First name"
                  required
                  fullWidth
                  darkMode={isDarkMode}
                  className={errors.firstName ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
                />
                {errors.firstName && (
                  <p className="text-sm text-red-600 mt-1">{errors.firstName}</p>
                )}
              </div>
              <div className="space-y-2">
                <InputField
                  label="Last Name"
                  name="lastName"
                  type="text"
                  value={userData.lastName}
                  onChange={handleInputChange}
                  placeholder="Last name"
                  required
                  fullWidth
                  darkMode={isDarkMode}
                  className={errors.lastName ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
                />
                {errors.lastName && (
                  <p className="text-sm text-red-600 mt-1">{errors.lastName}</p>
                )}
              </div>
            </div>
            {isQualifyRedirect && isWarehouseApiEnabled && (
              <div className="space-y-2">
                <InputField
                  label="Business Name"
                  name="businessName"
                  type="text"
                  value={userData.businessName || ''}
                  onChange={handleInputChange}
                  placeholder="Your courier business name"
                  fullWidth
                  darkMode={isDarkMode}
                />
              </div>
            )}
            <div className="space-y-2">
              <InputField
                label="Email"
                name="email"
                type="email"
                value={userData.email}
                onChange={handleInputChange}
                placeholder="Enter your email"
                required
                fullWidth
                  darkMode={isDarkMode}
                className={errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
              />
              {errors.email && (
                <p className="text-sm text-red-600 mt-1">{errors.email}</p>
              )}
            </div>
            <div className="space-y-2">
              <InputField
                label="Phone Number"
                name="phone"
                type="phone"
                value={userData.phone}
                onChange={handleInputChange}
                required
                fullWidth
                darkMode={isDarkMode}
                className={errors.phone ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
              />
              {errors.phone && (
                <p className="text-sm text-red-600 mt-1">{errors.phone}</p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Select your country and enter your number — we'll send WhatsApp/SMS updates there.
              </p>
            </div>
            <div className="space-y-2">
              <InputField
                label="Password"
                name="password"
                type="password"
                value={userData.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                required
                fullWidth
                  darkMode={isDarkMode}
                className={errors.password ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
              />
              {errors.password && (
                <p className="text-sm text-red-600 mt-1">{errors.password}</p>
              )}
            </div>
            {storeLocations.length > 1 && (
              <div className="space-y-2">
                <label htmlFor="storeLocationId" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Store Location <span className="text-red-500">*</span>
                </label>
                <select
                  id="storeLocationId"
                  name="storeLocationId"
                  value={userData.storeLocationId || ''}
                  onChange={handleInputChange}
                  className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm ${
                    isDarkMode
                      ? 'bg-gray-800 border-gray-600 text-gray-100 focus:ring-blue-500 focus:border-blue-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'
                  } ${errors.storeLocationId ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                >
                  <option value="">Select a store location</option>
                  {storeLocations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name} {location.isPrimaryLocation && '(Primary)'}
                    </option>
                  ))}
                </select>
                {errors.storeLocationId && (
                  <p className="text-sm text-red-600 mt-1">{errors.storeLocationId}</p>
                )}
              </div>
            )}
            {requireTrnOnSignup && (
              <div className="space-y-2">
                <div className="relative">
                  <InputField
                    label="TRN (Tax Registration Number)"
                    name="trnNumber"
                    type="text"
                    value={userData.trnNumber || ''}
                    onChange={handleInputChange}
                    placeholder="XXX-XXX-XXX"
                    required
                    fullWidth
                    darkMode={isDarkMode}
                    className={errors.trn ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
                    icon={<FileText className="h-5 w-5 text-gray-400" />}
                  />
                </div>
                {errors.trn && (
                  <p className="text-sm text-red-600 mt-1">{errors.trn}</p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Enter your 9-digit Tax Registration Number
                </p>
              </div>
            )}

            {requireAddressOnSignup && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Address</p>
                <InputField
                  label="Street Address"
                  name="street1"
                  type="text"
                  value={userData.street1 || ''}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  darkMode={isDarkMode}
                  className={errors.street1 ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
                />
                {errors.street1 && (
                  <p className="text-sm text-red-600 mt-1">{errors.street1}</p>
                )}
                <InputField
                  label="Apt / Suite (Optional)"
                  name="street2"
                  type="text"
                  value={userData.street2 || ''}
                  onChange={handleInputChange}
                  fullWidth
                  darkMode={isDarkMode}
                />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <InputField
                      label="City / Town"
                      name="city"
                      type="text"
                      value={userData.city || ''}
                      onChange={handleInputChange}
                      required
                      fullWidth
                      darkMode={isDarkMode}
                      className={errors.city ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
                    />
                    {errors.city && (
                      <p className="text-sm text-red-600 mt-1">{errors.city}</p>
                    )}
                  </div>
                  <InputField
                    label="Parish / State"
                    name="state"
                    type="text"
                    value={userData.state || ''}
                    onChange={handleInputChange}
                    fullWidth
                    darkMode={isDarkMode}
                  />
                </div>
                <InputField
                  label="Country"
                  name="country"
                  type="select"
                  value={userData.country || 'Jamaica'}
                  onChange={handleInputChange}
                  fullWidth
                  darkMode={isDarkMode}
                  options={getCountryNameOptions()}
                />
              </div>
            )}

            {isReferredByEnabled && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Were you referred by someone?
                </p>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="radio"
                      name="wasReferred"
                      checked={userData.wasReferred === true}
                      onChange={() => setUserData(prev => ({ ...prev, wasReferred: true }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    Yes
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="radio"
                      name="wasReferred"
                      checked={userData.wasReferred === false}
                      onChange={() => setUserData(prev => ({ ...prev, wasReferred: false, referrerQuery: '' }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    No
                  </label>
                </div>
                {userData.wasReferred && (
                  <InputField
                    label="Referrer's name or referral code"
                    name="referrerQuery"
                    type="text"
                    value={userData.referrerQuery || ''}
                    onChange={handleInputChange}
                    placeholder="Enter their name or referral code"
                    fullWidth
                    darkMode={isDarkMode}
                  />
                )}
              </div>
            )}

            {isReferralSourceEnabled && (
              <div className="space-y-2">
                <label htmlFor="referralSource" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  How did you hear about us?
                </label>
                <select
                  id="referralSource"
                  name="referralSource"
                  value={userData.referralSource || ''}
                  onChange={handleInputChange}
                  className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm ${
                    isDarkMode
                      ? 'bg-gray-800 border-gray-600 text-gray-100 focus:ring-blue-500 focus:border-blue-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'
                  }`}
                >
                  <option value="">Select an option</option>
                  {referralSourceOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Terms and Privacy Agreement */}
            <div className="space-y-3 pt-2">
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="terms-checkbox"
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => {
                      setAgreedToTerms(e.target.checked);
                      if (errors.agreements && e.target.checked && agreedToPrivacy) {
                        setErrors(prev => ({ ...prev, agreements: '' }));
                      }
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="terms-checkbox" className="text-gray-700 dark:text-gray-300">
                    I agree to the{' '}
                    <button
                      type="button"
                      onClick={() => setShowTermsModal(true)}
                      className="font-medium underline hover:no-underline text-gray-700 dark:text-blue-400"
                      style={!isDarkMode ? { color: primaryColor } : undefined}
                    >
                      Terms of Agreement
                    </button>
                  </label>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="privacy-checkbox"
                    type="checkbox"
                    checked={agreedToPrivacy}
                    onChange={(e) => {
                      setAgreedToPrivacy(e.target.checked);
                      if (errors.agreements && agreedToTerms && e.target.checked) {
                        setErrors(prev => ({ ...prev, agreements: '' }));
                      }
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="privacy-checkbox" className="text-gray-700 dark:text-gray-300">
                    I agree to the{' '}
                    <button
                      type="button"
                      onClick={() => setShowPrivacyModal(true)}
                      className="font-medium underline hover:no-underline text-gray-700 dark:text-blue-400"
                      style={!isDarkMode ? { color: primaryColor } : undefined}
                    >
                      Privacy Policy
                    </button>
                  </label>
                </div>
              </div>

              {errors.agreements && (
                <p className="text-sm text-red-600 mt-1">{errors.agreements}</p>
              )}
            </div>

            {siteKey && (
              <div className="flex justify-center">
                <Turnstile
                  ref={widgetRef}
                  siteKey={siteKey}
                  onSuccess={onSuccess}
                  onError={onError}
                  onExpire={onExpire}
                  options={{ theme: isDarkMode ? 'dark' : 'light', size: 'flexible' }}
                />
              </div>
            )}
            {turnstileError && (
              <p className="text-sm text-red-600 text-center">CAPTCHA failed to load. Please refresh the page.</p>
            )}

            <Button
              type="submit"
              variant="primary"
              disabled={!isFormValid}
              isLoading={isLoading}
              fullWidth
              color={primaryColor}
              textColor="white"
              hoverColor={primaryColor}
              className="flex items-center justify-center"
            >
              {isLoading ? 'Creating account...' : (
                <>
                  Continue
                  <ArrowRightIcon className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
            <div className="mt-4 text-center">
              <button
                type="button"
                className="text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                onClick={onToggleMode}
                style={!isDarkMode ? { color: primaryColor } : undefined}
              >
                Already have an account? Sign in
              </button>
            </div>
          </form>
        </>
      ) : (
        <OTPInput
          onComplete={verifyCode}
          onResend={handleResendCode}
          title="Verify your email address"
          description={`We've sent a verification code to ${userData.email}. Please enter the code below to verify your account. If you don't see it, check your spam or junk folder.`}
          verifyText="Verify & Create Account"
          primaryColor={primaryColor}
          isLoading={isLoading}
          disabled={isSendingVerification}
          isResending={isSendingVerification}
        />
      )}

      {/* Terms of Agreement Modal */}
      <Modal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        title="Terms of Agreement"
        size="large"
      >
        <div className="prose dark:prose-invert max-w-none max-h-[60vh] overflow-y-auto">
          {legalDocument?.termsOfService && legalDocument?.termsOfService !== '<p></p>' ? (
            <>
              <div className="raw-html" dangerouslySetInnerHTML={{ __html: legalDocument.termsOfService }} />
              {legalDocument.lastUpdatedTerms && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-6">
                  Last updated: {new Date(legalDocument.lastUpdatedTerms).toLocaleDateString()}
                </p>
              )}
            </>
          ) : (
            <>
              <h3 className="text-lg font-semibold mb-4">1. Acceptance of Terms</h3>
              <p className="mb-4">
                By accessing and using this service, you accept and agree to be bound by the terms and provision of this agreement.
                If you do not agree to abide by the above, please do not use this service.
              </p>

              <h3 className="text-lg font-semibold mb-4">2. Use License</h3>
              <p className="mb-4">
                Permission is granted to temporarily use this service for personal, non-commercial transitory viewing only.
                This is the grant of a license, not a transfer of title, and under this license you may not:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Modify or copy the materials</li>
                <li>Use the materials for any commercial purpose</li>
                <li>Attempt to decompile or reverse engineer any software contained in the service</li>
                <li>Remove any copyright or other proprietary notations from the materials</li>
              </ul>

              <h3 className="text-lg font-semibold mb-4">3. User Account</h3>
              <p className="mb-4">
                You are responsible for maintaining the confidentiality of your account and password.
                You agree to accept responsibility for all activities that occur under your account or password.
              </p>

              <h3 className="text-lg font-semibold mb-4">4. Service Availability</h3>
              <p className="mb-4">
                We reserve the right to modify or discontinue, temporarily or permanently, the service with or without notice.
                We shall not be liable to you or any third party for any modification, suspension, or discontinuance of the service.
              </p>

              <h3 className="text-lg font-semibold mb-4">5. Limitation of Liability</h3>
              <p className="mb-4">
                In no event shall we be liable for any damages (including, without limitation, damages for loss of data or profit,
                or due to business interruption) arising out of the use or inability to use the service.
              </p>

              <h3 className="text-lg font-semibold mb-4">6. Governing Law</h3>
              <p className="mb-4">
                These terms shall be governed and construed in accordance with the laws of the jurisdiction in which the service operates,
                without regard to its conflict of law provisions.
              </p>

              <p className="text-sm text-gray-600 dark:text-gray-400 mt-6">
                Last updated: {new Date().toLocaleDateString()}
              </p>
            </>
          )}
        </div>
      </Modal>

      {/* Privacy Policy Modal */}
      <Modal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        title="Privacy Policy"
        size="large"
      >
        <div className="prose dark:prose-invert max-w-none max-h-[60vh] overflow-y-auto">
          {legalDocument?.privacyPolicy && legalDocument?.privacyPolicy !== '<p></p>' ? (
            <>
              <div className="raw-html" dangerouslySetInnerHTML={{ __html: legalDocument.privacyPolicy }} />
              {legalDocument.lastUpdatedPrivacy && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-6">
                  Last updated: {new Date(legalDocument.lastUpdatedPrivacy).toLocaleDateString()}
                </p>
              )}
            </>
          ) : (
            <>
              <h3 className="text-lg font-semibold mb-4">1. Information We Collect</h3>
              <p className="mb-4">
                We collect information you provide directly to us, including:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Name and contact information (email, phone number)</li>
                <li>Account credentials</li>
                <li>Delivery addresses and preferences</li>
                <li>Payment information</li>
                <li>Order history and communications</li>
              </ul>

              <h3 className="text-lg font-semibold mb-4">2. How We Use Your Information</h3>
              <p className="mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Process and fulfill your orders</li>
                <li>Send you order confirmations and updates</li>
                <li>Communicate with you about products, services, and promotions</li>
                <li>Improve our services and customer experience</li>
                <li>Detect and prevent fraud or abuse</li>
              </ul>

              <h3 className="text-lg font-semibold mb-4">3. Information Sharing</h3>
              <p className="mb-4">
                We do not sell, trade, or otherwise transfer your personally identifiable information to outside parties.
                This does not include trusted third parties who assist us in operating our service, conducting our business,
                or servicing you, so long as those parties agree to keep this information confidential.
              </p>

              <h3 className="text-lg font-semibold mb-4">4. Data Security</h3>
              <p className="mb-4">
                We implement a variety of security measures to maintain the safety of your personal information.
                Your personal information is contained behind secured networks and is only accessible by a limited number
                of persons who have special access rights to such systems.
              </p>

              <h3 className="text-lg font-semibold mb-4">5. Cookies and Tracking</h3>
              <p className="mb-4">
                We use cookies and similar tracking technologies to track activity on our service and hold certain information.
                Cookies are files with small amount of data which may include an anonymous unique identifier.
              </p>

              <h3 className="text-lg font-semibold mb-4">6. Your Rights</h3>
              <p className="mb-4">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Access, update, or delete your personal information</li>
                <li>Object to processing of your personal information</li>
                <li>Request restriction of processing your personal information</li>
                <li>Request transfer of your personal information</li>
                <li>Withdraw consent at any time</li>
              </ul>

              <h3 className="text-lg font-semibold mb-4">7. Contact Us</h3>
              <p className="mb-4">
                If you have any questions about this Privacy Policy, please contact us through the contact information
                provided in your account settings.
              </p>

              <p className="text-sm text-gray-600 dark:text-gray-400 mt-6">
                Last updated: {new Date().toLocaleDateString()}
              </p>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default CreateAccountForm;
